/* eslint-disable no-underscore-dangle */
import ReactGA from 'react-ga';
import uaAnalytics from '@utils/uaAnalytics';
import { sendEvents } from '@events/sendAnalytics';

class Player {
  constructor(classDetails, actions, user) {
    this.timer = null;
    this.sentSuccessfulView = false;
    this.sentSuccessfulView15 = false;
    this.prevUACallTime = 0;
    this.watchEvent = {};
    this.visibilityChangeEvent = null;
    this.classDetails = classDetails;
    this.actions = actions;
    this.user = user;
  }

  getHiddenProp = () => {
    const prefixes = ['webkit', 'moz', 'ms', 'o'];

    // if 'hidden' is natively supported just return it
    if ('hidden' in document) return 'hidden';

    // otherwise loop over all the known prefixes until we find one
    for (let i = 0; i < prefixes.length; i += 1) {
      if (`${prefixes[i]}Hidden` in document) return `${prefixes[i]}Hidden`;
    }

    // otherwise it's not supported
    return null;
  };

  isHidden = () => {
    const prop = this.getHiddenProp();
    if (!prop) return false;

    return document[prop];
  };

  oneSecWatched = (event) => {
    ReactGA.event({
      category:
        'Video Player' +
        `-${this.classDetails.videoType === 2 ? 'youtube' : 'unacademy'}`,
      action: 'Play Start',
      label: this.classDetails.uid,
      nonInteraction: true
    });

    this.sendAnalyticsEvent('Lesson Play', {
      'Lesson Duration': this.classDetails.lessonDuration,
      Source: 'Direct',
      'Lesson Start Mode': 'UserPlay',
      ...this.getEventData()
    });
    //   window.branch && window.branch.logEvent('Lesson Play');
    //   WebEngageEvents.trackEvent('Lesson Play', {
    //     'Lesson Id': `${this.props.post.id}`,
    //     'Lesson Name': `${this.props.post.title}`,
    //     'Lesson Duration': this.props.post.duration,
    //     'Course ID': `${this.props.post.collection.uid}`,
    //     'Course Name': `${this.props.post.collection.title}`,
    //     Source:
    //       this.props.router.locationBeforeTransitions.query.source ||
    //       (this.props.insideModal ? 'Modal' : 'Direct'),
    //     'Lesson Start Mode': this.shouldAutoPlay() ? 'AutoPlay' : 'UserPlay'
    //   });

    // see saving one http call
    this.videoPlayerView(event);

    this.videoPlayerWatchStart(event, {
      force: true
    });

    if (this.classDetails.uid) {
      this.actions.updateViewCount();
    }
  };

  tenSecWatched = () => {
    if (!this.classDetails.hasWatched) {
      this.actions.videoWatched();
    }
  };

  watchEventCallback = (secondsWatched, event, force = false) => {
    this.watchEvent = event;
    if (secondsWatched > 300 && !this.sentSuccessfulView) {
      this.sendSuccessfulViewEvent();
    }
    if (secondsWatched > 900 && !this.sentSuccessfulView15) {
      this.sendSuccessfulView15MinsEvent();
    }
    if (
      force ||
      (secondsWatched >= 10 && secondsWatched >= this.prevUACallTime + 10)
    ) {
      this.watchEvent.TOTAL_WATCH_TIME_END =
        secondsWatched - this.prevUACallTime;
      this.prevUACallTime = secondsWatched;
      this.sendWatchEvent(event);
      this.sendLessonSecondWatchArray(event);
      return secondsWatched;
    }
  };

  videoEnd = (event) => {
    this.sendLessonSecondWatchArray(event);
    this.watchEventCallback(event.TOTAL_RUN_TIME, event, true);
    this.actions.onVideoEnd();
  };

  checkAPI = () => {
    if (window.UnPlayer) {
      let hasUpdatedViewCount = false;
      let hasUpdatedVideoWatchedByUser = false;
      const handler = {
        [this.classDetails.key]: (video) => {
          video.bind('WATCHED', (event) => {
            this.watchEvent = event;

            if (!hasUpdatedViewCount && video.secondsWatched() >= 1) {
              if (this.classDetails.uid) {
                hasUpdatedViewCount = true;
              }
              this.oneSecWatched(event);
            }

            if (!hasUpdatedVideoWatchedByUser && video.secondsWatched() >= 10) {
              hasUpdatedVideoWatchedByUser = true;
              this.tenSecWatched(event);
            }

            if (hasUpdatedViewCount) {
              this.watchEventCallback(video.secondsWatched(), event);
            }
          });

          video.bind('END', (event) => {
            this.videoEnd(event);
          });

          //   video.bind('PAUSE', event => {
          //     this.videoPaused(event);
          //   });

          //   video.ready(() => {
          //     this.videoReady();
          //   });
        }
      };
      window._unq.push(handler);
      clearInterval(this.timer);
    }
  };

  setup = () => {
    clearInterval(this.timer);
    if (this.classDetails.videoType !== 2) {
      this.timer = setInterval(this.checkAPI, 1000);
    }
    const visProp = this.getHiddenProp();
    if (visProp) {
      this.visibilityChangeEvent = `${visProp.replace(
        /[H|h]idden/,
        ''
      )}visibilitychange`;

      if (window && window.document)
        document.addEventListener(this.visibilityChangeEvent, this.onPageHide);
    }
  };

  sendLessonSecondWatchArray = (watchEvent) => {
    const event = { ...watchEvent };
    this.sendEventUa(
      'lesson_watch_status',
      this.classDetails.videoType,
      event,
      { force: true }
    );
  };

  sendWatchEvent = (event) => {
    // this event is not send with force True because next player watch we are sending with force True
    this.videoPlayerWatchEnd(event, {
      force: false
    });
    this.videoPlayerWatched(event);
    // setting events to to undefined so same event wont get send again on page hide or page unload
    this.watchEvent = undefined;
  };

  videoPlayerWatchEnd = (event, options = { force: true }) => {
    this.sendEventUa(
      'player_watch_end',
      this.classDetails.videoType,
      event,
      options
    );
  };

  videoPlayerWatched = (event, options = { force: true }) => {
    this.sendEventUa(
      'player_video_watch',
      this.classDetails.videoType,
      event,
      options
    );
  };

  videoPlayerWatchStart = (event, options = { force: true }) => {
    this.sendEventUa(
      'player_watch_start',
      this.classDetails.videoType,
      event,
      options
    );
  };

  videoPlayerView = (event, options = { force: true }) => {
    this.sendEventUa('video_view', this.classDetails.videoType, event, options);
  };

  sendEventUa = (eventName, type, event, options) => {
    const finalType = type === 2 ? 'youtube' : 'unacademy';
    const watchEvent = event || this.watchEvent;
    if (watchEvent) {
      watchEvent.uid = this.classDetails.uid;
      if (finalType) {
        watchEvent.video_player_type = finalType === 'youtube' ? 2 : 1;
      }
      if (this.classDetails.collection)
        watchEvent.collection_slug = this.classDetails.collection.slug;
      if (this.classDetails.author)
        watchEvent.user_uid = this.classDetails.author.uid;
      else watchEvent.player_type = 'normal';

      uaAnalytics.sendUa(eventName, event, options);

      if (finalType === 'youtube') {
        // this.setState({ ytUpdateLastSendTime: event.TOTAL_RUN_TIME });
      }
    }
  };

  sendAnalyticsEvent = (name, data) => {
    sendEvents(name, data);
  };

  getEventData = () => {
    return {
      'Lesson ID': this.classDetails.uid,
      'Learner Username': this.user?.username,
      'Course Id': this.classDetails.programme?.uid,
      'Course Name': this.classDetails.programme?.name,
      'Educator Username': this.classDetails.author?.username,
      'Educator Id': this.classDetails.author?.uid,
      'Learner Id': this.user?.uid,
      'Learner UID': this.user?.uid,
      'Player Type':
        this.classDetails.videoType === 2 ? 'Youtube' : 'Unacademy',
      'Is Special': false,
      'Is Plus': false,
      'Goal Id': this.classDetails?.programme?.goalUid,
      'Goal Name': this.classDetails?.collection?.goal_name
    };
  };

  sendSuccessfulViewEvent = () => {
    if (!this.sentSuccessfulView) {
      this.sentSuccessfulView = true;
      this.sendAnalyticsEvent('Successful View', this.getEventData());
    }
  };

  sendSuccessfulView15MinsEvent = () => {
    if (!this.sentSuccessfulView15) {
      this.sentSuccessfulView15 = true;
      this.sendAnalyticsEvent(
        'Successful View Fifteen Minutes',
        this.getEventData()
      );
    }
  };

  // Tab switch or Component unmount.
  onPageHide = () => {
    if (this.isHidden()) {
      if (typeof this.watchEvent !== 'undefined') {
        const event = this.watchEvent;
        this.watchEventCallback(event.TOTAL_RUN_TIME, event, true);
        this.sendLessonSecondWatchArray(event);
      }
    }
  };

  unmount = () => {
    clearInterval(this.timer);
    document.removeEventListener(this.visibilityChangeEvent, this.onPageHide);
    if (this.watchEvent && typeof this.watchEvent !== 'undefined') {
      this.watchEventCallback(this.watchEvent.TOTAL_RUN_TIME, this.watchEvent);
    }
    this.sendLessonSecondWatchArray(this.watchEvent);
    this.watchEvent = {};
  };
}

export default Player;
